import { Button } from '@massilia/ui'
import { Link } from '@tanstack/react-router'
import { AlertTriangle, RefreshCw, Search } from 'lucide-react'

interface FlightErrorCardProps {
  onRetry?: () => void
  type?: 'error' | 'no-results'
}

export function FlightErrorCard({ onRetry, type = 'no-results' }: FlightErrorCardProps) {
  const isError = type === 'error'

  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white px-8 py-12 shadow-sm">
      <div className={`mb-6 flex h-20 w-20 items-center justify-center rounded-full ${isError ? 'bg-red-50' : 'bg-amber-50'}`}>
        {isError ? (
          <AlertTriangle className="h-10 w-10 text-red-500" />
        ) : (
          <Search className="h-10 w-10 text-amber-500" />
        )}
      </div>

      <h2 className="mb-2 text-xl font-semibold text-gray-900">
        {isError ? 'Erreur de recherche' : 'Aucun résultat trouvé'}
      </h2>

      <p className="mb-6 max-w-md text-center text-gray-600">
        {isError
          ? "Une erreur est survenue lors de la recherche de vols. Cela peut être dû à un problème temporaire."
          : "Nous n'avons pas pu trouver de vols correspondant à votre recherche. Essayez avec des dates différentes ou une destination alternative."}
      </p>

      <div className={`mb-8 rounded-lg p-4 ${isError ? 'bg-red-50' : 'bg-sky-50'}`}>
        <p className={`mb-3 text-sm font-medium ${isError ? 'text-red-700' : 'text-sky-700'}`}>
          {isError ? 'Que faire ?' : 'Quelques conseils :'}
        </p>
        <ul className={`space-y-2 text-sm ${isError ? 'text-red-600' : 'text-sky-600'}`}>
          {isError ? (
            <>
              <li className="flex items-start gap-2">
                <RefreshCw className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Réessayez dans quelques instants</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Si le problème persiste, contactez le support</span>
              </li>
            </>
          ) : (
            <>
              <li className="flex items-start gap-2">
                <span>📅</span>
                <span>Essayez des dates plus flexibles (quelques jours d&apos;écart)</span>
              </li>
              <li className="flex items-start gap-2">
                <span>✈️</span>
                <span>Vérifiez que les villes de départ et d&apos;arrivée sont correctes</span>
              </li>
              <li className="flex items-start gap-2">
                <span>💬</span>
                <span>Utilisez le chatbot pour affiner votre recherche avec notre aide</span>
              </li>
            </>
          )}
        </ul>
      </div>

      <div className="flex gap-3">
        {onRetry && (
          <Button
            className={isError ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-sky-200 text-sky-600 hover:bg-sky-50'}
            onClick={onRetry}
            variant="outline"
          >
            <RefreshCw className="size-4" />
            Réessayer
          </Button>
        )}
        <Link to="/">
          <Button className="bg-brand-600 hover:bg-brand-700">
            Nouvelle recherche
          </Button>
        </Link>
      </div>
    </div>
  )
}
